import { Header } from '@components/Header';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import { organizationsKeys } from 'app/queries/organizations/organizationsKeys';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { EACCEPTED_AUDIO_FORMATS } from 'app/types/enums/EACCEPTED_AUDIO_FORMATS';
import { EACCEPTED_DOCUMENT_FORMATS } from 'app/types/enums/EACCEPTED_DOCUMENT_FORMATS';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { createFetchOrganizationSummaryQueryFn } from './api/fetchOrganizationSummary';
import { createFetchRepositoryQueryFn } from './api/fetchRepository';
import { MainBlock, TForm } from './components/blocks/MainBlock';
import { Content } from './styles/Content';
import { RootWrapperDiv } from './styles/RootWrapperDiv';

export const AdminRepositoryPage = () => {
  const { call } = useAPI();
  const params = useParams<{ id: string }>();
  const repositoryId = params.id ?? '';
  const [form, setForm] = useState<TForm>({
    id: repositoryId,
    organizationId: '',
    name: '',
    slug: '',
    notes: '',
    audioFiles: [],
    documentFiles: [],
    assignee: null,
  });

  const repositoryQuery = useQuery({
    queryKey: repositoriesKeys.detail(repositoryId),
    queryFn: createFetchRepositoryQueryFn(call),
    enabled: repositoryId !== '',
  });
  const repository = repositoryQuery.data;

  const organizationId = repository?.organizationId ? String(repository.organizationId) : '';
  const organizationQuery = useQuery({
    queryKey: organizationsKeys.summary(organizationId),
    queryFn: createFetchOrganizationSummaryQueryFn(call),
    enabled: organizationId !== '',
  });
  const organizationSlug = organizationQuery.data?.slug ?? '';

  useEffect(() => {
    if (!repository) {
      return;
    }

    const audioFilesFromServer = repository.Attachments
      ? repository.Attachments.filter((file) =>
          EACCEPTED_AUDIO_FORMATS.includes(`.${file.fileName.split('.').pop()?.toLowerCase()}`),
        )
      : [];

    const documentFilesFromServer = repository.Attachments
      ? repository.Attachments.filter((file) =>
          EACCEPTED_DOCUMENT_FORMATS.includes(`.${file.fileName.split('.').pop()?.toLowerCase()}`),
        )
      : [];

    setForm({
      id: repository.id,
      organizationId: String(repository.organizationId),
      name: repository.name,
      slug: repository.slug,
      notes: String(repository.notes),
      assignee: repository.Assignee,
      audioFiles: audioFilesFromServer,
      documentFiles: documentFilesFromServer,
    });
  }, [repository]);

  const isLoading =
    (repositoryId !== '' && repositoryQuery.isLoading) || (organizationId !== '' && organizationQuery.isLoading);

  return (
    <>
      <Helmet>
        <title>{form.name} - AutoScript</title>
      </Helmet>
      <Header title={form.name} backRoute={routes.adminOrganizationDetails.make(form?.organizationId)} />

      <LinearProgressLoader active={isLoading} />
      <Content>
        <form className="flex flex-col">
          <RootWrapperDiv>
            {form.name && repositoryId ? (
              <MainBlock
                repositoryId={repositoryId}
                organizationSlug={organizationSlug}
                documentAttachments={form.documentFiles}
                audioAttachments={form.audioFiles}
                isFilesLoading={isLoading}
                form={form}
                setForm={setForm}
              />
            ) : null}
          </RootWrapperDiv>
        </form>
      </Content>
    </>
  );
};
